/**
 * @fileoverview Top Navigation Bar Component
 * @module ctl-topbar
 * @version 1.0.0
 * 
 * @description
 * A responsive top navigation bar with support for branding, title, navigation
 * items, and action buttons. Automatically handles overflow and can be made sticky.
 * 
 * @example
 * <ctl-topbar title="My Application" sticky>
 *   <div slot="brand">
 *     <img src="logo.svg" alt="Logo">
 *   </div>
 *   <nav slot="nav">
 *     <a href="/home">Home</a>
 *     <a href="/about">About</a>
 *   </nav>
 *   <div slot="actions">
 *     <button>Login</button>
 *   </div>
 * </ctl-topbar>
 */

import "../foundation/ctl-typography.js";
import "../foundation/ctl-divider.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-topbar-height: 56px;
    --ctl-topbar-pad-x: var(--ctl-space-3);

    display:block;
    box-sizing: border-box;
    font-family: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    color: var(--ctl-color-text);
  }

  header{
    height: var(--ctl-topbar-height);
    display:flex;
    align-items:center;
    gap: var(--ctl-space-3);
    padding: 0 var(--ctl-topbar-pad-x);
    border-bottom: 1px solid var(--ctl-color-border);
    background: color-mix(in srgb, var(--ctl-color-surface) 95%, transparent);
    backdrop-filter: var(--ctl-blur-md);
    -webkit-backdrop-filter: var(--ctl-blur-md);
    box-shadow: var(--ctl-shadow-sm);
    position: relative;
    z-index: var(--ctl-z-topbar);
  }

  :host([sticky]) header{
    position: sticky;
    top: 0;
    z-index: var(--ctl-z-topbar);
  }

  .brand{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    min-width: 0;
    flex: 0 0 auto;
  }

  .title{
    display:flex;
    align-items:baseline;
    gap: var(--ctl-space-2);
    min-width: 0;
  }

  .titleText{
    font-weight: var(--ctl-font-weight-bold);
    font-size: var(--ctl-font-size-base);
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: -0.01em;
  }

  .nav{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }
  .nav:empty{ display:none; }

  .spacer{
    flex: 1 1 auto;
  }

  .actions{
    display:flex;
    align-items:center;
    justify-content:flex-end;
    gap: var(--ctl-space-2);
    flex: 0 0 auto;
  }
  .actions:empty{ display:none; }

  .custom{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    min-width: 0;
    flex: 1 1 auto;
  }
  .custom:empty{ display:none; }

  /* Slot wrappers */
  .slotBrand ::slotted(*){ display:inline-flex; align-items:center; gap: var(--ctl-space-2); }
  .slotNav ::slotted(*){ display:inline-flex; align-items:center; gap: var(--ctl-space-2); }
  .slotActions ::slotted(*){ display:inline-flex; align-items:center; gap: var(--ctl-space-2); }

  /* Hide empty title wrapper when no title and no brand slot content */
  :host([no-title]) .title{ display:none; }

  /* Hide title section (divider + text) when no title is provided */
  :host(:not([has-title])) .title{ display:none; }

  /* Divider styling */
  ctl-divider{
    margin: 0 var(--ctl-space-2);
  }
  :host([collapsed]) ctl-divider{ display:none; }
</style>

<header part="header" role="banner">
  <div class="brand" part="brand">
    <span class="slotBrand"><slot name="brand"></slot></span>

    <div class="title" part="title">
      <ctl-divider orientation="vertical" length="20px" inset="0px"></ctl-divider>
      <span class="titleText" id="title"></span>
    </div>
  </div>

  <div class="nav slotNav" part="nav">
    <slot name="nav"></slot>
  </div>

  <div class="custom" part="custom">
    <slot></slot>
  </div>

  <div class="spacer" part="spacer"></div>

  <div class="actions slotActions" part="actions">
    <slot name="actions"></slot>
  </div>
</header>
`;

/**
 * CtlTopbar - Top Navigation Bar Component
 * 
 * @class
 * @extends HTMLElement
 * 
 * @attr {string} title - Title text to display in the topbar
 * @attr {boolean} sticky - Whether the topbar should stick to the top when scrolling
 * 
 * @slot brand - Brand/logo content (left side)
 * @slot nav - Navigation links (center-left)
 * @slot (default) - Custom content (center)
 * @slot actions - Action buttons (right side)
 */
export class CtlTopbar extends HTMLElement {
  static get observedAttributes() {
    return ["title", "sticky"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._titleEl = this.shadowRoot.getElementById("title");
    this._brandSlot = this.shadowRoot.querySelector('slot[name="brand"]');

    this._onBrandSlotChange = this._onBrandSlotChange.bind(this);
  }

  connectedCallback() {
    this._brandSlot?.addEventListener("slotchange", this._onBrandSlotChange);
    this._sync();
  }

  disconnectedCallback() {
    this._brandSlot?.removeEventListener("slotchange", this._onBrandSlotChange);
  }

  attributeChangedCallback() {
    this._sync();
  }

  get title() { return this.getAttribute("title") || ""; }
  set title(v) {
    v == null || v === "" ? this.removeAttribute("title") : this.setAttribute("title", String(v));
  }

  get sticky() { return this.hasAttribute("sticky"); }
  set sticky(v) { v ? this.setAttribute("sticky", "") : this.removeAttribute("sticky"); }

  _onBrandSlotChange() {
    this._sync();
  }

  _sync() {
    const title = (this.getAttribute("title") || "").trim();
    this._titleEl.textContent = title;

    const brandAssigned = (this._brandSlot?.assignedNodes({ flatten: true }) || [])
      .some((n) => (n.nodeType === Node.ELEMENT_NODE) || (n.nodeType === Node.TEXT_NODE && String(n.textContent || "").trim()));

    // Track whether a title is present (for showing/hiding the divider + title)
    this.toggleAttribute("has-title", !!title);

    // If neither title nor brand exists, hide the title row entirely
    this.toggleAttribute("no-title", !title && !brandAssigned);
  }
}

customElements.define("ctl-topbar", CtlTopbar);
